import React, { useEffect, useRef, useState } from 'react';
import { Tooltip as AntdTooltip } from 'antd';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { mixGetV2 } from 'services/mixin';
import { EquityCurveProps } from '../../Metrix/type';
import { InfoIcon } from '@phosphor-icons/react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface MonteCarloSimulatorProps {
  report: any;
  liveTrade: any;
}

const MonteCarloSimulator: React.FC<MonteCarloSimulatorProps> = ({ report, liveTrade }) => {
  const { t } = useTranslation();

  // State from EquitySimulation
  const [equityCurves, setEquityCurves] = useState<EquityCurveProps[]>([]);
  const [avgDataTable, setAvgDataTable] = useState<any>();
  const [capital, setCapital] = useState(0);
  const [win, setWin] = useState<string>("50");
  const [rrr, setRRR] = useState<string>("1");
  const [iterations, setIterations] = useState(100);
  const [lines, setLines] = useState(10);
  const [risk, setRisk] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const hasInitializedListData = useRef(false);

  const sendData = async () => {
    setIsLoading(true);
    try {
      const res = await mixGetV2(
        `/tool/simulator-matrix-rr-risk?matrix_balance_size=10&matrix_number_trades=10&matrix_rr=10&matrix_list_winrate=0.4%2C%200.5%2C%200.6%2C%200.7&matrix_list_risk_percent=0.002%2C%200.0025%2C%200.003%2C%200.0035%2C%200.004&chart_balance_size=${capital}&chart_lines=${lines}&chart_number_trades=${iterations}&chart_winrate=${Number(win || 0) / 100}&chart_rr=${rrr}&chart_risk_percent=${risk / 100}`
      );

      if (!res || typeof res !== 'object') {
        console.warn('Simulator returned invalid response:', res);
        setAvgDataTable(undefined);
        setEquityCurves([]);
        return;
      }

      let transformedCurves: EquityCurveProps[] = [];
      if (Array.isArray((res as any).equity_curves)) {
        transformedCurves = (res as any).equity_curves.map((curve: any) => ({
          trade: curve.trade,
          line1: curve.line_1,
          line2: curve.line_2,
          line3: curve.line_3,
          line4: curve.line_4,
          line5: curve.line_5,
          line6: curve.line_6,
          line7: curve.line_7,
          line8: curve.line_8,
          line9: curve.line_9,
          line10: curve.line_10,
          avg_equity: curve.avg_equity,
        }));
      }

      const avgEquityTable = (res as any).analyze_equity_curves && (res as any).analyze_equity_curves.avg_equity
        ? (res as any).analyze_equity_curves.avg_equity
        : undefined;

      setAvgDataTable(avgEquityTable);
      setEquityCurves(transformedCurves);
    } catch (error) {
      console.error('Error while calling simulator API:', error);
      setAvgDataTable(undefined);
      setEquityCurves([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (report?.data && !hasInitializedListData.current) {
      const newWin = parseFloat((report?.data.win_rate * 100)?.toFixed(1));
      const newRRR = parseFloat((report?.data.mean_RRR)?.toFixed(2));
      const newCapital = parseFloat((report?.data.account_info?.initial_balance)?.toFixed(2));
      console.log('MonteCarloSimulator: Initializing with report data:', { report });
      if (!isNaN(newWin) && !isNaN(newRRR) && !isNaN(newCapital)) {
        setWin(String(Number(newWin.toFixed(1)) || 0));
        setRRR(String(Number(newRRR.toFixed(2)) || 0));
        setCapital(newCapital || 0);
        hasInitializedListData.current = true;
      }
    }
  }, [report]);

  useEffect(() => {
    if (hasInitializedListData.current) {
      sendData();
    }
  }, [win, rrr, capital]);

  // Chart Logic
  const labels = equityCurves.map(c => c.trade.toString());
  const lineColors = [
    'rgba(59, 130, 246, 0.4)',
    'rgba(20, 184, 166, 0.4)',
    'rgba(99, 102, 241, 0.4)',
    'rgba(245, 158, 11, 0.4)',
    'rgba(236, 72, 153, 0.4)',
    'rgba(139, 92, 246, 0.4)',
    'rgba(16, 185, 129, 0.4)',
    'rgba(249, 115, 22, 0.4)',
    'rgba(107, 114, 128, 0.4)',
  ];

  const datasets = Array.from({ length: Math.min(lines, 10) }, (_, i) => ({
    label: `Sim ${i + 1}`,
    data: equityCurves.map(c => (c as any)[`line${i + 1}`]),
    borderColor: lineColors[i % lineColors.length],
    borderWidth: 1,
    pointRadius: 0,
    fill: false,
    tension: 0,
  }));

  // Add average line
  if (equityCurves.length > 0) {
    datasets.push({
      label: 'Average',
      data: equityCurves.map(c => c.avg_equity),
      borderColor: 'rgba(15, 23, 42, 0.9)',
      borderWidth: 3,
      pointRadius: 0,
      fill: false,
      tension: 0,
    } as any);
  }

  const chartData = {
    labels,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#1e293b',
        bodyColor: '#475569',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        padding: 12,
        boxPadding: 4,
        usePointStyle: true,
        callbacks: {
          label: (context: any) => {
            return ` ${context.dataset.label}: $${Number(context.raw).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 10
        }
      },
      y: {
        display: true,
        grid: { color: '#f1f5f9' },
        ticks: {
          callback: (value: any) => `$${Number(value).toLocaleString()}`
        }
      },
    },
  };

  return (
    <div className="bg-white rounded-2xl shadow-premium border border-slate-100 overflow-hidden mt-6">
      <div className="p-6 sm:p-8 border-b border-slate-50">
        <div className="flex justify-start gap-2 items-center mb-2 z-10 relative">
          <h2 className="!text-lg font-bold !text-slate-900 !mb-0">Mô phỏng Vốn chủ sở hữu</h2>
          <AntdTooltip title="Biểu đồ mô phỏng đường cong vốn (Equity Curve) trong tương lai, dựa trên các thông số Vốn, Win Rate, RRR, Số giao dịch và Rủi ro bạn nhập bên dưới. Giúp bạn hình dung xu hướng tăng/giảm vốn và đánh giá mức độ rủi ro trước khi giao dịch thật.">
            <div className="w-8 h-8 rounded border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-50">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 16v-4"></path>
                <path d="M12 8h.01"></path>
              </svg>
            </div>
          </AntdTooltip>
        </div>
        <p className="!text-sm !text-slate-500 mb-8 max-w-2xl">
          Dự báo đường cong vốn trong tương lai dựa trên dữ liệu hiệu suất trong quá khứ để rà soát rủi ro.
        </p>

        {avgDataTable && (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
            {[
              { label: 'Max Equity', value: `$${avgDataTable.max_equity?.toLocaleString()}`, color: 'text-emerald-600', tooltip: "montecarlo.tooltip.max_equity" },
              { label: 'Min Equity', value: `$${avgDataTable.min_equity?.toLocaleString()}`, color: 'text-rose-600', tooltip: "montecarlo.tooltip.min_equity" },
              { label: 'Max DD (%)', value: `${avgDataTable.max_drawdown_percent?.toFixed(2)}%`, color: 'text-amber-600', tooltip: "montecarlo.tooltip.max_dd" },
              { label: 'Avg DD (%)', value: `${avgDataTable.avg_drawdown_percent?.toFixed(2)}%`, color: 'text-slate-600', tooltip: "montecarlo.tooltip.avg_dd" },
              { label: 'Max Win streak', value: avgDataTable.max_consecutive_winners, color: 'text-emerald-600', tooltip: "montecarlo.tooltip.max_win_streak" },
              { label: 'Max Loss streak', value: avgDataTable.max_consecutive_losers, color: 'text-rose-600', tooltip: "montecarlo.tooltip.max_loss_streak" },
            ].map((stat, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                <div className="!text-[10px] flex flex-row gap-2 font-bold !text-slate-400 uppercase tracking-widest mb-1">
                  <span>{stat.label}</span>
                  <AntdTooltip title={t(stat.tooltip) || 'Lỗ cao nhất trong kỳ báo cáo.'}>
                    <InfoIcon size={16} />
                  </AntdTooltip>
                </div>
                <div className={`!text-sm font-bold ${stat.color}`}>{stat.value}</div>
              </div>
            ))}
          </div>
        )}

        <div className="relative h-[400px] w-full mb-8">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10 backdrop-blur-[1px]">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500"></div>
                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Đang tính toán...</span>
              </div>
            </div>
          )}
          {equityCurves.length > 0 ? (
            <Line data={chartData} options={options} />
          ) : !isLoading && (
            <div className="h-full flex flex-col items-center justify-center border-2 border-dashed border-slate-100 rounded-2xl bg-slate-50/50">
              <div className="p-4 bg-white rounded-full shadow-sm mb-4">
                <svg className="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <span className="text-sm font-medium text-slate-400">Không có dữ liệu mô phỏng</span>
            </div>
          )}
        </div>

        {/* Premium Input Form */}
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100 items-end">
          <div>
            <div className="flex justify-start gap-2 items-center mb-2 z-10 relative">
              <label className="block !text-[10px] font-bold !text-slate-500 uppercase tracking-wider !mb-0 ml-1">Vốn ($)</label>
              <AntdTooltip title="Số vốn ban đầu dùng làm điểm xuất phát cho mô phỏng đường cong vốn.">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
              </AntdTooltip>
            </div>
            <input
              type="text"
              value={capital.toLocaleString('en-US', { maximumFractionDigits: 2 })}
              onChange={(e) => setCapital(Number(e.target.value.replace(/,/g, '')))}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 !text-sm font-mono-num font-semibold !text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none shadow-sm transition-all"
            />
          </div>
          <div>
            <div className="flex justify-start gap-2 items-center mb-2 z-10 relative">
              <label className="block !text-[10px] font-bold !text-slate-500 uppercase tracking-wider !mb-0 ml-1">Win Rate (%)</label>
              <AntdTooltip title="Tỷ lệ thắng giả định cho mỗi lệnh, dùng làm cơ sở tính toán kết quả mô phỏng.">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
              </AntdTooltip>
            </div>
            <input
              type="number"
              value={win}
              onChange={(e) => setWin(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 !text-sm font-mono-num font-semibold !text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none shadow-sm transition-all"
            />
          </div>
          <div>
            <div className="flex justify-start gap-2 items-center mb-2 z-10 relative">
              <label className="block !text-[10px] font-bold !text-slate-500 uppercase tracking-wider !mb-0 ml-1">RRR</label>
              <AntdTooltip title="Risk Reward Ratio — tỷ lệ giữa mức lãi kỳ vọng và mức rủi ro chấp nhận cho mỗi lệnh (Lãi ÷ Rủi ro).">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
              </AntdTooltip>
            </div>
            <input
              type="number"
              step="0.01"
              value={rrr}
              onChange={(e) => setRRR(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 !text-sm font-mono-num font-semibold !text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none shadow-sm transition-all"
            />
          </div>
          <div>
            <div className="flex justify-start gap-2 items-center mb-2 z-10 relative">
              <label className="block !text-[10px] font-bold !text-slate-500 uppercase tracking-wider !mb-0 ml-1">Số giao dịch</label>
              <AntdTooltip title="Tổng số lệnh giả lập sẽ được chạy trong mô phỏng đường cong vốn.">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
              </AntdTooltip>
            </div>
            <input
              type="number"
              value={iterations}
              onChange={(e) => setIterations(Number(e.target.value))}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 !text-sm font-mono-num font-semibold !text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none shadow-sm transition-all"
            />
          </div>
          <div>

            <div className="flex justify-start gap-2 items-center mb-2 z-10 relative">
              <label className="block !text-[10px] font-bold !text-slate-500 uppercase tracking-wider !mb-0 ml-1">Rủi ro (%)</label>
              <AntdTooltip title="Phần trăm vốn chấp nhận rủi ro cho mỗi lệnh trong quá trình mô phỏng.">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
              </AntdTooltip>
            </div>
            <input
              type="number"
              value={risk}
              onChange={(e) => setRisk(Number(e.target.value))}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 !text-sm font-mono-num font-semibold !text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none shadow-sm transition-all"
            />
          </div>
          <div>
            <button
              onClick={sendData}
              disabled={isLoading}
              className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-50 !text-white font-bold !py-2.5 !px-4 !rounded-xl shadow-md transition-all active:scale-[0.98] !text-sm flex items-center justify-center gap-2"
            >
              {!isLoading && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {isLoading ? 'Đang chạy...' : 'Chạy Mô Phỏng'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonteCarloSimulator;
